export interface LoopOptions {
  /** Simulation steps per second. */
  hz?: number;
  /**
   * Most steps run for one frame. Past this the clock is allowed to slip
   * rather than the loop trying to catch up.
   */
  maxStepsPerFrame?: number;
  update?: (dt: number) => void;
  render?: (alpha: number) => void;
  /** Frames per second at most when `start`ed; 0, the default, is every display frame. Also settable at runtime. */
  maxFps?: number;
}

/**
 * Whether a display frame at `at` runs under a cap of `maxFps`, and the deadline after it. Paced by deadline,
 * not by the gap since the last run: each run moves `due` on by exactly one interval, so over time the loop runs
 * at the cap whatever the display's rate — a gap test can only land on whole display frames, and a 30 cap on a
 * 43 Hz display ran every other frame, at 21. A frame counts as on time within half the display's own frame
 * (`displayMs`) of its deadline. A loop more than an interval behind starts its deadlines again from now.
 */
export function paceFrame(at: number, due: number, maxFps: number, displayMs = 1000 / 60): { run: boolean; due: number } {
  if (!(maxFps > 0)) return { run: true, due: at };
  if (at < due - displayMs / 2) return { run: false, due };
  const interval = 1000 / maxFps;
  return { run: true, due: at - due > interval ? at + interval : due + interval };
}

const DEFAULT_HZ = 60;
const DEFAULT_MAX_STEPS = 5;
/**
 * Time constant for the frame-rate average, in milliseconds.
 *
 * Roughly a third of a second of history: long enough that the number is
 * readable rather than flickering through every value between 58 and 62, short
 * enough that a real drop shows up straight away.
 */
const FPS_SMOOTHING_MS = 300;
/**
 * A gap longer than this is a stall, not a frame rate — a backgrounded tab, a
 * breakpoint, a long GC — and folding it into the average would peg the
 * display at zero for seconds after everything was fine again.
 */
const FPS_STALL_MS = 1000;

/**
 * A fixed-timestep game loop.
 *
 * Fixed rather than variable, because physics integrated against a variable
 * delta is not deterministic: the same inputs on a machine running at 144Hz
 * and one running at 30Hz produce different trajectories, and a collision that
 * resolves on one drops through the floor on the other. A fixed step makes the
 * simulation independent of how fast the display happens to be.
 *
 * The leftover time is handed to `render` as `alpha`, 0..1 between the last
 * two steps, so a renderer can interpolate and stay smooth even though the
 * simulation is landing on discrete ticks.
 *
 * `maxStepsPerFrame` is the spiral-of-death guard. A tab returning from the
 * background has a large accumulated delta; without a cap the loop tries to
 * simulate all of it, takes longer than a frame doing so, accumulates more,
 * and never recovers. Past the cap the remainder is dropped and the clock
 * simply slips, which is the only outcome that recovers.
 */
export class Loop {
  #hz: number;
  #step: number;
  #maxSteps: number;
  #accumulator = 0;
  #last = 0;
  #handle = 0;
  #running = false;
  /** Simulated seconds since the loop started, in whole steps. */
  #elapsed = 0;
  #steps = 0;
  /** Smoothed milliseconds per frame. `fps` is derived from it. */
  #frameMs = 0;
  #frames = 0;

  update: (dt: number) => void;
  render: (alpha: number) => void;
  /**
   * Frames per second at most, from `start`'s own frame source; 0 is uncapped. A display frame that comes too
   * soon is skipped whole — nothing stepped, nothing rendered — and its time is taken up by the next. `advance`
   * called by hand is never capped.
   */
  maxFps: number;

  /** When the next capped frame is due, and the display's own frame length, smoothed. */
  #due = 0;
  #lastSeen = 0;
  #displayMs = 1000 / 60;

  constructor(options: LoopOptions = {}) {
    this.#hz = Math.max(1, options.hz ?? DEFAULT_HZ);
    this.#step = 1 / this.#hz;
    this.#maxSteps = Math.max(1, options.maxStepsPerFrame ?? DEFAULT_MAX_STEPS);
    this.update = options.update ?? (() => {});
    this.render = options.render ?? (() => {});
    this.maxFps = options.maxFps ?? 0;
  }

  get running(): boolean {
    return this.#running;
  }

  /** Steps per second. */
  get hz(): number {
    return this.#hz;
  }
  set hz(v: number) {
    this.#hz = Math.max(1, v);
    this.#step = 1 / this.#hz;
  }

  /** Length of one simulation step, in seconds. */
  get step(): number {
    return this.#step;
  }

  /** Simulated seconds since the loop started. */
  get elapsed(): number {
    return this.#elapsed;
  }

  /** Steps run since the loop started. */
  get steps(): number {
    return this.#steps;
  }

  /** How far between the last two steps the display is, 0..1. */
  get alpha(): number {
    return this.#step > 0 ? this.#accumulator / this.#step : 0;
  }

  /**
   * Frames per second, smoothed.
   *
   * The *display* rate — how often `render` is being called — not the
   * simulation rate, which is fixed at `hz` by construction and would only
   * ever tell you what you already set.
   *
   * Derived from `frameMs` rather than averaged on its own. Averaging the two
   * separately gives two answers that disagree: the mean of a reciprocal is
   * not the reciprocal of the mean, so a run of fast frames with the odd long
   * one holds the frame-rate average up while the millisecond average climbs,
   * and the readout says something like "141 fps · 14.3 ms". Smoothing the
   * time and dividing once means a stutter shows in both, which is the whole
   * reason for looking.
   */
  get fps(): number {
    return this.#frameMs > 0 ? 1000 / this.#frameMs : 0;
  }

  /** Milliseconds per frame, smoothed. */
  get frameMs(): number {
    return this.#frameMs;
  }

  /** Frames rendered since the loop started. */
  get frames(): number {
    return this.#frames;
  }

  start(): void {
    if (this.#running) return;
    this.#running = true;
    this.#last = now();
    this.#due = this.#last;
    this.#lastSeen = this.#last;
    const frame = (): void => {
      if (!this.#running) return;
      this.#handle = requestAnimationFrame(frame);
      const at = now();
      const seen = at - this.#lastSeen;
      this.#lastSeen = at;
      if (seen > 0 && seen < FPS_STALL_MS) this.#displayMs += (seen - this.#displayMs) * 0.1;
      const paced = paceFrame(at, this.#due, this.maxFps, this.#displayMs);
      this.#due = paced.due;
      if (paced.run) this.advance(at);
    };
    this.#handle = requestAnimationFrame(frame);
  }

  stop(): void {
    if (!this.#running) return;
    this.#running = false;
    if (this.#handle !== 0) cancelAnimationFrame(this.#handle);
    this.#handle = 0;
  }

  /**
   * Advance to wall-clock time `at`, in milliseconds.
   *
   * Public and separate from `start` so a caller can drive the loop from its
   * own frame source — and so the whole thing can be tested without a browser
   * or a real clock, which is the only way a fixed-step loop's edge cases are
   * checkable at all.
   */
  advance(at: number): void {
    const elapsedMs = at - this.#last;
    const delta = Math.max(0, elapsedMs / 1000);
    this.#last = at;
    this.#accumulator += delta;
    this.#sampleRate(elapsedMs);

    let ran = 0;
    while (this.#accumulator >= this.#step && ran < this.#maxSteps) {
      this.update(this.#step);
      this.#accumulator -= this.#step;
      this.#elapsed += this.#step;
      this.#steps++;
      ran++;
    }
    // Still behind after the cap: give up on the backlog rather than fall
    // further behind trying to chase it.
    if (this.#accumulator >= this.#step) this.#accumulator = 0;

    this.render(this.alpha);
  }

  /**
   * Fold one frame's length into the running average.
   *
   * Weighted by how long the frame actually took, so the smoothing constant
   * means the same thing at 30fps as at 144 — a plain per-frame blend would
   * smooth over five times as much wall-clock history on a slow machine as on
   * a fast one, and the number would settle at a different speed on each.
   */
  #sampleRate(elapsedMs: number): void {
    this.#frames++;
    if (elapsedMs <= 0 || elapsedMs > FPS_STALL_MS) return;
    if (this.#frameMs === 0) {
      // Seed from the first real frame rather than easing up from zero, or the
      // display spends the first second climbing to the truth.
      this.#frameMs = elapsedMs;
      return;
    }
    const t = 1 - Math.exp(-elapsedMs / FPS_SMOOTHING_MS);
    this.#frameMs += (elapsedMs - this.#frameMs) * t;
  }

  /** Forget the accumulated time, e.g. after a pause. */
  reset(at = now()): void {
    this.#accumulator = 0;
    this.#last = at;
  }
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}
